import { Box, useTheme } from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '../../mui-wrapper-components/Button/Button';
import { Icon } from '../../mui-wrapper-components/Icon/Icon';
import { Tooltip } from '../../mui-wrapper-components/Tooltip/Tooltip';

const COPY_ICON_SIZE = 16;
const COPY_BTN_TOP_RIGHT_POS = 14;
const COPY_BTN_BORDER_RADIUS = 2;

const CopyIcon = () => {
  const theme = useTheme();

  return (
    <Icon
      id="copy-to-clipboard-icon"
      icon="copy"
      color="info"
      height={COPY_ICON_SIZE}
      width={COPY_ICON_SIZE}
      sx={{ fill: theme.palette.primary.main }}
    />
  );
};

export type CopyToClipboardContentProps = {
  id: string;
  copyText: string;
  copyButtonLabel: string;
  copiedButtonLabel: string;
  copiedTooltipTitle: string;
  children: ReactNode;
  buttonWidth?: number;
};

export const CopyToClipboardContent = ({
  id,
  copyText,
  copyButtonLabel,
  copiedButtonLabel,
  copiedTooltipTitle,
  children,
  buttonWidth = 115,
}: CopyToClipboardContentProps) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [copied]);

  const onCopy = async () => {
    if (copied || copyText.length === 0) {
      return;
    }

    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
    } catch (error) {
      console.error('Copy to clipboard failed:', error);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        id={`${id}-tooltip`}
        title={copiedTooltipTitle}
        open={copied}
        width="auto"
        placement="top"
      >
        <Button
          id={id}
          displayLabel={copied ? copiedButtonLabel : copyButtonLabel}
          onClickHandler={onCopy}
          btnType="secondary"
          variant="outlined"
          sx={{
            position: 'absolute',
            top: COPY_BTN_TOP_RIGHT_POS,
            right: COPY_BTN_TOP_RIGHT_POS,
            borderRadius: COPY_BTN_BORDER_RADIUS,
            width: buttonWidth,
          }}
          startIcon={<CopyIcon />}
        />
      </Tooltip>

      {children}
    </Box>
  );
};
